import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Record a short voice note via MediaRecorder. `start()` asks for the mic and begins; `stop()`
 * resolves the recorded audio as a Blob (or null if nothing was captured); `cancel()` throws the
 * take away. The mic stream is always released. Used by the messaging composer.
 */
export interface VoiceRecorder {
  supported: boolean;
  recording: boolean;
  seconds: number;
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
  cancel: () => void;
}

export function useVoiceRecorder(): VoiceRecorder {
  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof MediaRecorder !== 'undefined';
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const teardown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recRef.current = null;
    setRecording(false);
    setSeconds(0);
  }, []);

  const start = useCallback(async () => {
    if (!supported || recRef.current) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    chunksRef.current = [];
    const rec = new MediaRecorder(stream);
    rec.ondataavailable = (e) => {
      if (e.data.size) chunksRef.current.push(e.data);
    };
    rec.start();
    recRef.current = rec;
    setRecording(true);
    setSeconds(0);
    timerRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
  }, [supported]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    const rec = recRef.current;
    if (!rec) return null;
    const mime = rec.mimeType || 'audio/webm';
    const blob = await new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(chunksRef.current, { type: mime }));
      rec.stop();
    });
    teardown();
    return blob.size ? blob : null;
  }, [teardown]);

  const cancel = useCallback(() => {
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      try {
        rec.stop();
      } catch {
        // already stopped
      }
    }
    teardown();
  }, [teardown]);

  // Release the mic if the component unmounts mid-recording.
  useEffect(() => teardown, [teardown]);

  return { supported, recording, seconds, start, stop, cancel };
}
